import React, { useRef } from 'react';
import { KPICard } from '../ui';
import { useHRKeyMetrics } from '../../hooks/useHRQueries';
import LoadingSpinner from '../ui/LoadingSpinner';
import ChartControls from '../ui/ChartControls';
import useChartGuide from '../../hooks/useChartGuide';
import { useFavoriteToggle } from '../../hooks/useFavoriteToggle';
import { CHART_GUIDES } from '../../constants/chartGuides';

interface KeyPerformanceMetricsProps {
  branchId?: number;
  showLoading?: boolean;
  onError?: (error: Error) => void;
}

const KeyPerformanceMetrics: React.FC<KeyPerformanceMetricsProps> = ({ 
  branchId,
  showLoading = true,
  onError 
}) => {
  const chartRef = useRef<HTMLDivElement>(null);
  const { data, isLoading, error } = useHRKeyMetrics(branchId);
  const { openGuide } = useChartGuide();
  const { handleFavoriteToggle, isFavorited } = useFavoriteToggle();
  
  // Component metadata
  const componentId = 'key-performance-metrics';
  const componentTitle = 'آمار کلیدی عملکرد';

  React.useEffect(() => {
    if (error && onError) {
      onError(error as Error);
    }
  }, [error, onError]);

  if (isLoading && showLoading) {
    return (
      <div className="flex items-center justify-center min-h-[200px]">
        <LoadingSpinner variant="minimal" size="lg" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="card-base border border-status-error p-4">
        <p className="text-status-error">خطا در بارگذاری آمار کلیدی عملکرد</p>
      </div>
    );
  }

  if (!data) {
    return null;
  }

  return (
    <div ref={chartRef} className="card-base card-padding">
      <div className="mb-6">
        <ChartControls
          componentId={componentId}
          componentTitle={componentTitle}
          componentType="kpi"
          dashboardSource="hr"
          onInfo={() => openGuide(CHART_GUIDES.keyPerformanceMetrics)}
          onFavorite={() => handleFavoriteToggle({
            componentId,
            componentTitle,
            dashboardSource: 'hr',
            componentType: 'kpi',
            description: 'آمار کلیدی عملکرد پرسنل',
            props: { branchId, showLoading, onError },
            gridLayout: {
              containerClass: 'grid grid-cols-1 gap-6'
            }
          })}
          isFavorited={isFavorited(componentId)}
          chartRef={chartRef}
          className="mb-4"
        />
        <h3 className="title-card">{componentTitle}</h3>
      </div>
      <div className="grid grid-cols-1 gap-3 sm:gap-4">
        <KPICard
          title="کل عملیات"
          value={data.totalOperations}
          variant="primary"
          accentStyle="corner"
          className="p-2 min-h-0 transition-colors"
          aria-label={`کل عملیات: ${data.totalOperations}`}
        />
        
        <KPICard
          title="میانگین عملیات هر پرسنل"
          value={data.avgOperationsPerStaff}
          variant="info"
          accentStyle="corner"
          className="p-2 min-h-0 transition-colors"
          aria-label={`میانگین عملیات هر پرسنل: ${data.avgOperationsPerStaff}`}
        />
        
        <KPICard
          title="میانگین نظرات مثبت"
          value={`${data.avgPositiveReviews.toFixed(2)}%`}
          variant={data.avgPositiveReviews >= 85 ? 'success' : data.avgPositiveReviews >= 70 ? 'info' : data.avgPositiveReviews >= 60 ? 'warning' : 'error'}
          accentStyle="corner"
          className="p-2 min-h-0 transition-colors"
          aria-label={`میانگین نظرات مثبت: ${data.avgPositiveReviews}%`}
        />
      </div>
    </div>
  );
};

export default KeyPerformanceMetrics;